import React, { useState, useEffect } from 'react';
import { Box, Text, useColorModeValue, FormControl, FormLabel, Input, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, VStack, Card, CardBody, Tag, TagLabel, Select } from '@chakra-ui/react';
import getCSRFtoken from '../utils/getCSRFtoken';
import CollapsibleSection from './shared/CollapsibleSection';
import LoadingSpinner from './shared/LoadingSpinner';
import ErrorMessage from './shared/ErrorMessage';
import EmptyState from './shared/EmptyState';
import { useToast } from '../hooks/useToast';
import Breadcrumbs from './shared/Breadcrumbs';
import SubframeFilterBuilder from './SubframeFilterBuilder';
import fetchWithAuth from '../utils/fetchWithAuth';

function AudiencesList() {
    const csrfToken = getCSRFtoken();
    const [audiences, setAudiences] = useState([]);
    const [populations, setPopulations] = useState([]);
    const [newAudience, setNewAudience] = useState({
        name: '',
        description: '',
        population: '',
        subframe_filters: [],
    });
    const [subframeSize, setSubframeSize] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAudiences, setShowAudiences] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const bgColor = useColorModeValue('background.shade1');
    const color = useColorModeValue('theme.black');
    const toast = useToast();

    useEffect(() => {
        fetchAudiences();
        fetchPopulations();
    }, []);

    const fetchAudiences = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth('/api/audiences/');
            if (!response.ok) throw new Error('Failed to fetch audiences');
            const data = await response.json();
            const auds = data.results || [];
            setAudiences(auds);
            setError(null);
        } catch (error) {
            setError('Failed to fetch audiences. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchPopulations = async () => {
        try {
            const response = await fetchWithAuth('/api/populations/');
            if (!response.ok) throw new Error('Failed to fetch populations');
            const data = await response.json();
            setPopulations(data.results || []);
        } catch (error) {
            console.error('Error fetching populations:', error);
        }
    };

    const handleInputChange = (e) => {
        setNewAudience((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAddAudience = async () => {
        // Basic validation
        if (!newAudience.population) {
            toast.showError('Validation Error', 'Please select a population');
            return;
        }

        if (!newAudience.name || !newAudience.name.trim()) {
            toast.showError('Validation Error', 'Please enter a name for this audience');
            return;
        }

        if (subframeSize === 0) {
            toast.showError('Validation Error', 'Your filters resulted in 0 individuals. Please adjust your criteria.');
            return;
        }

        const dataToSend = {
            name: newAudience.name.trim(),
            description: newAudience.description || '',
            population: parseInt(newAudience.population, 10),
            subframe_filters: newAudience.subframe_filters.length > 0 ? newAudience.subframe_filters : null,
        };
    
        try {
            const response = await fetchWithAuth('/api/audiences/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) throw new Error('Failed to create audience');

            const data = await response.json();
            setAudiences((prev) => [...prev, data]);
            setNewAudience({
                name: '',
                description: '',
                population: '',
                subframe_filters: [],
            });
            setSubframeSize(null);
            setShowCreateForm(false);
            fetchAudiences();
            toast.showSuccess('Success', 'Audience created successfully');
        } catch (error) {
            console.error('Error creating audience:', error);
            toast.showError('Error', 'Failed to create audience. Please check your inputs.');
        }
    };
    

    const toggleAudiences = () => {
        setShowAudiences(!showAudiences);
    };

    const toggleCreateForm = () => {
        setShowCreateForm(!showCreateForm);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box bg={bgColor} color={color} minHeight="100vh">
            <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
                <Breadcrumbs
                    items={[
                        { label: 'Home', path: '/' },
                        { label: 'Audiences' }
                    ]}
                />
                <CollapsibleSection
                    title="View Audiences"
                    isOpen={showAudiences}
                    onToggle={toggleAudiences}
                >
                {audiences.length === 0 ? (
                    <EmptyState message="No audiences available." />
                ) : (
                    <Card>
                        <CardBody>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Name</Th>
                                        <Th>Population</Th>
                                        <Th isNumeric>Size</Th>
                                        <Th>Filters</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {audiences.map((audience) => (
                                        <Tr key={audience.id} _hover={{ bg: 'background.shade3' }}>
                                            <Td>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontWeight="medium">{audience.name}</Text>
                                                    {audience.description && (
                                                        <Text fontSize="xs" color="gray.500">
                                                            {audience.description}
                                                        </Text>
                                                    )}
                                                </VStack>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm" color="gray.600">
                                                    {audience.population_name || 'Unknown'}
                                                </Text>
                                            </Td>
                                            <Td isNumeric>
                                                <VStack align="end" spacing={0}>
                                                    <Badge colorScheme={audience.subframe_size > 0 ? 'green' : 'gray'}>
                                                        {audience.subframe_size?.toLocaleString() || 0}
                                                    </Badge>
                                                    {audience.subframe_filters && audience.subframe_filters.length > 0 && (
                                                        <Text fontSize="xs" color="gray.500">
                                                            of {audience.population_size?.toLocaleString() || 0} ({Math.round((audience.subframe_size / audience.population_size) * 100)}%)
                                                        </Text>
                                                    )}
                                                </VStack>
                                            </Td>
                                            <Td>
                                                <VStack align="start" spacing={1}>
                                                    {audience.subframe_filters && audience.subframe_filters.length > 0 ? (
                                                        <>
                                                            {audience.subframe_filters.slice(0, 2).map((filter, idx) => (
                                                                <Tag key={idx} size="sm" colorScheme="purple">
                                                                    <TagLabel>{filter.column} {filter.operator} {filter.value}</TagLabel>
                                                                </Tag>
                                                            ))}
                                                            {audience.subframe_filters.length > 2 && (
                                                                <Text fontSize="xs" color="gray.500">
                                                                    +{audience.subframe_filters.length - 2} more
                                                                </Text>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {audience.gender && (
                                                                <Tag size="sm" colorScheme="purple">
                                                                    <TagLabel>{audience.gender}</TagLabel>
                                                                </Tag>
                                                            )}
                                                            {audience.age_range && (
                                                                <Tag size="sm" colorScheme="blue">
                                                                    <TagLabel>{audience.age_range}</TagLabel>
                                                                </Tag>
                                                            )}
                                                            {!audience.gender && !audience.age_range && (
                                                                <Text fontSize="sm" color="gray.400">None</Text>
                                                            )}
                                                        </>
                                                    )}
                                                </VStack>
                                            </Td>
                                            <Td>
                                                <Badge colorScheme={audience.poll ? 'green' : 'gray'}>
                                                    {audience.poll ? 'In Use' : 'Available'}
                                                </Badge>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>
                )}
                </CollapsibleSection>
                <CollapsibleSection
                    title="Create New Audience"
                    isOpen={showCreateForm}
                    onToggle={toggleCreateForm}
                >
                    <Box>
                        <FormControl id="population" mb={4} isRequired>
                            <FormLabel>Select Population</FormLabel>
                            {populations.length > 0 ? (
                                <Select name="population" value={newAudience.population} onChange={handleInputChange} placeholder="Choose a population">
                                    {populations.map((population) => (
                                        <option key={population.id} value={population.id}>
                                            {population.name} ({population.num_individuals?.toLocaleString() || 0} individuals)
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Text>Please add populations first.</Text>
                            )}
                        </FormControl>
                        {/* Subframe filtering - the main action */}
                        {newAudience.population && (
                            <Box mb={4}>
                                <SubframeFilterBuilder
                                    populationId={newAudience.population}
                                    value={newAudience.subframe_filters}
                                    onChange={(filters) => setNewAudience(prev => ({ ...prev, subframe_filters: filters }))}
                                    onSubframeSizeChange={setSubframeSize}
                                />
                            </Box>
                        )}

                        <FormControl id="name" mb={4} isRequired>
                            <FormLabel>Audience Name</FormLabel>
                            <Input
                                type="text"
                                name="name"
                                value={newAudience.name}
                                onChange={handleInputChange}
                                placeholder="Enter audience name"
                            />
                        </FormControl>
                        <FormControl id="description" mb={4}>
                            <FormLabel>Description (optional)</FormLabel>
                            <Input type="text" name="description" value={newAudience.description} onChange={handleInputChange} placeholder="Describe this audience" />
                        </FormControl>

                        <Button
                            onClick={handleAddAudience}
                            variant="primary"
                            size="lg"
                            width="full"
                            mt={4}
                            isDisabled={!newAudience.population || !newAudience.name || !newAudience.name.trim() || subframeSize === null || subframeSize === 0}
                        >
                            Create Audience {subframeSize !== null && subframeSize > 0 && `(${subframeSize.toLocaleString()} individuals)`}
                        </Button>
                    </Box>
                </CollapsibleSection>
            </Box>
        </Box>
    );
}

export default AudiencesList;